<template>
	<div class="cw-content" :class="'cw-theme-' + theme">
		<div v-if="cw.loading" class="cw-loader"><div class="cw-spinner"></div></div>

		<template v-else>
		<!-- OVERVIEW -->
		<div v-show="active === 'overview'">
			<div class="cw-section">
				<div class="cw-figs">
					<div class="cw-fig"><div class="cw-fig__l">Faktureret</div><div class="cw-fig__v">{{ money(total('invoiced')) }}</div></div>
					<div class="cw-fig"><div class="cw-fig__l">Tilgode</div><div class="cw-fig__v">{{ money(total('tilgode')) }}</div></div>
					<div class="cw-fig"><div class="cw-fig__l">Tilbudt</div><div class="cw-fig__v">{{ money(total('tilbudt')) }}</div></div>
					<div class="cw-fig"><div class="cw-fig__l">Emballage</div><div class="cw-fig__v">{{ money(cw.packaging.total_value) }}</div></div>
				</div>
			</div>

			<div class="cw-section">
				<div class="cw-head">Henvendelser, tilbud &amp; ordrer</div>
				<apexchart v-if="hasSeries" type="area" height="200" :options="chartOptions" :series="chartSeries" />
				<div class="cw-empty" v-else>Ingen historik endnu</div>
			</div>

			<div class="cw-section">
				<div class="cw-head">Ansvarlige</div>
				<div v-if="responsible.length">
					<div class="cw-line" v-for="r in responsible" :key="'r' + r.id"><span>{{ r.name || '-' }}</span><span class="cw-muted">{{ r.count }}</span></div>
				</div>
				<div class="cw-empty" v-else>Ingen opgaver endnu</div>
			</div>

			<div class="cw-section">
				<div class="cw-head">Medarbejdere</div>
				<div v-if="crew.length">
					<div class="cw-line" v-for="r in crew" :key="'c' + r.id"><span>{{ r.name || '-' }}</span><span class="cw-muted">{{ r.count }} · {{ Math.round(r.hours) }}t</span></div>
				</div>
				<div class="cw-empty" v-else>Ingen flytninger endnu</div>
			</div>
		</div>

		<!-- ORDERS -->
		<div v-show="active === 'orders'">
			<input class="cw-search" v-model="orderSearch" placeholder="Søg ordrer…" />
			<div class="cw-row" v-for="o in filteredOrders" :key="'o' + o.id" @click="$emit('open', { kind: 'order', id: o.id })">
				<div class="cw-row__main">#{{ o.num }}<span class="cw-row__sub">{{ fmt(o.created) }} · {{ o.status || '-' }}</span></div>
				<div class="cw-row__val">{{ money(o.total) }}</div>
			</div>
			<div class="cw-empty" v-if="!filteredOrders.length">Ingen ordrer</div>
		</div>

		<!-- QUOTES -->
		<div v-show="active === 'quotes'">
			<input class="cw-search" v-model="quoteSearch" placeholder="Søg tilbud…" />
			<div class="cw-row" v-for="q in filteredQuotes" :key="'q' + q.id" @click="$emit('open', { kind: 'quote', id: q.id })">
				<div class="cw-row__main">#{{ q.num }}<span class="cw-row__sub">{{ fmt(q.created) }} · {{ q.status || '-' }}</span></div>
				<div class="cw-row__val">{{ money(quoteTotal(q)) }}</div>
			</div>
			<div class="cw-empty" v-if="!filteredQuotes.length">Ingen tilbud</div>
		</div>

		<!-- TASKS -->
		<div v-show="active === 'tasks'">
			<input class="cw-search" v-model="taskSearch" placeholder="Søg opgaver…" />
			<div class="cw-row" v-for="t in filteredTasks" :key="'t' + t.id" @click="$emit('open', { kind: 'task', id: t.id, type: t.type })">
				<div class="cw-row__main">#{{ t.num }}<span class="cw-row__sub">{{ t.type }} · {{ fmt(t.due_start_from) }}</span></div>
				<div class="cw-row__val">{{ t.status || (t.closed ? 'Afsluttet' : 'Åben') }}</div>
			</div>
			<div class="cw-empty" v-if="!filteredTasks.length">Ingen opgaver</div>
		</div>

		<!-- PACKAGING -->
		<div v-show="active === 'packaging'">
			<div class="cw-figs">
				<div class="cw-fig"><div class="cw-fig__l">Værdi</div><div class="cw-fig__v">{{ money(cw.packaging.total_value) }}</div></div>
				<div class="cw-fig"><div class="cw-fig__l">Enheder</div><div class="cw-fig__v">{{ cw.packaging.total_qty }}</div></div>
				<div class="cw-fig"><div class="cw-fig__l">Sidste varsel</div><div class="cw-fig__v">{{ cw.packaging.last_notice ? fmt(cw.packaging.last_notice) : '-' }} · {{ cw.packaging.notice_count }}</div></div>
			</div>
			<div class="cw-segline">
				<div class="cw-seg" :class="{ 'is-on': packMode === 'all' }" @click="packMode = 'all'">Alle</div>
				<div class="cw-seg" :class="{ 'is-on': packMode === 'relevant' }" @click="packMode = 'relevant'">Relevante</div>
			</div>
			<div class="cw-row" v-for="p in packItems" :key="'pk' + p.id" :class="{ 'is-muted': Number(p.qty) === 0 }">
				<div class="cw-row__main">{{ p.label || '-' }}<span class="cw-row__sub">{{ money(p.value) }}<template v-if="Number(p.qty) !== 0 && cw.packaging.overdue"> · forfalden</template><template v-if="Number(p.qty) !== 0 && cw.packaging.frozen"> · fastfrosset</template></span></div>
				<div class="cw-row__val">{{ p.qty }} stk</div>
			</div>
			<div class="cw-empty" v-if="!packItems.length">Ingen emballage</div>
		</div>

		<!-- ACTIVITY -->
		<div v-show="active === 'activity'">
			<div class="cw-chips">
				<div class="cw-chip" v-for="k in activityKinds" :key="k" :class="{ 'is-on': actTypes[k] }" @click="actTypes[k] = !actTypes[k]">{{ typeLabel(k) }}</div>
				<div class="cw-chip" v-if="anyActSelected" @click="actClear">Ryd</div>
			</div>
			<input class="cw-search" v-model="actSearch" placeholder="Søg aktivitet…" />
			<div class="cw-row" v-for="(e, i) in filteredActivity" :key="'e' + i" :class="{ 'is-click': actOpenable(e) }" @click="actOpen(e)">
				<div class="cw-row__main">{{ e.title }}<span class="cw-row__sub" v-if="e.sub">{{ e.sub }}</span></div>
				<div class="cw-row__val cw-muted">{{ fmtTime(e.ts) }}</div>
			</div>
			<div class="cw-empty" v-if="!filteredActivity.length">Ingen aktivitet</div>
		</div>
		</template>
	</div>
</template>

<script>
	export default {
		name: 'customer-content',
		props: {
			cw: { type: Object, required: true },
			activity: { type: Array, default: () => [] },
			active: { type: String, default: 'overview' },
			theme: { type: String, default: 'dark' }
		},
		data() {
			return {
				orderSearch: '',
				quoteSearch: '',
				taskSearch: '',
				packMode: 'relevant',
				actSearch: '',
				activityKinds: ['order', 'quote', 'task', 'email', 'sms'],
				actTypes: { order: false, quote: false, task: false, email: false, sms: false }
			};
		},
		computed: {
			responsible() { return (this.cw.stats && this.cw.stats.responsible) || []; },
			crew() { return (this.cw.stats && this.cw.stats.crew) || []; },
			series() { return (this.cw.stats && this.cw.stats.series) || []; },
			hasSeries() { return this.series.some(s => Number(s.leads) || Number(s.quotes) || Number(s.orders)); },
			chartSeries() {
				return [
					{ name: 'Henvendelser', data: this.series.map(s => Number(s.leads)) },
					{ name: 'Tilbud', data: this.series.map(s => Number(s.quotes)) },
					{ name: 'Ordrer', data: this.series.map(s => Number(s.orders)) }
				];
			},
			chartOptions() {
				const dark = this.theme === 'dark';
				return {
					chart: { id: 'cw-funnel', toolbar: { show: false }, fontFamily: 'Montserrat, sans-serif', foreColor: dark ? '#a0a3ac' : '#8a8d96', background: 'transparent', redrawOnParentResize: true },
					colors: ['#a0a3ac', '#ffcf00', '#f7223f'],
					dataLabels: { enabled: false },
					stroke: { curve: 'smooth', width: 2 },
					fill: { type: 'gradient', gradient: { opacityFrom: 0.3, opacityTo: 0 } },
					grid: { borderColor: dark ? '#2d2f36' : '#ececec' },
					legend: { position: 'top', horizontalAlign: 'left', labels: { colors: dark ? '#a0a3ac' : '#8a8d96' } },
					xaxis: { categories: this.series.map(s => new Date(s.month + '-01').toLocaleDateString('da-DK', { month: 'short', year: '2-digit' })), axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
					yaxis: { min: 0, forceNiceScale: true, labels: { formatter: v => Math.round(v) } },
					tooltip: { theme: dark ? 'dark' : 'light' }
				};
			},
			filteredOrders() { return this.filterRows(this.cw.orders, this.orderSearch, o => [o.num, o.status, this.fmt(o.created)]); },
			filteredQuotes() { return this.filterRows(this.cw.quotes, this.quoteSearch, q => [q.num, q.status, this.fmt(q.created)]); },
			filteredTasks() { return this.filterRows(this.cw.tasks, this.taskSearch, t => [t.num, t.type, t.status, this.fmt(t.due_start_from)]); },
			packItems() {
				if (this.packMode === 'relevant') return this.cw.packaging.items.filter(p => Number(p.qty) !== 0);
				return this.cw.packaging.items;
			},
			anyActSelected() { return this.activityKinds.some(k => this.actTypes[k]); },
			filteredActivity() {
				const q = this.actSearch.trim().toLowerCase();
				const any = this.anyActSelected;
				return this.activity.filter(e => (!any || this.actTypes[e.type]) && (!q || (e.title + ' ' + (e.sub || '')).toLowerCase().indexOf(q) !== -1));
			}
		},
		methods: {
			total(key) { return (this.cw.stats && this.cw.stats.totals && this.cw.stats.totals[key]) || 0; },
			money(v) { return new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0) + ' kr.'; },
			fmt(v) { return v ? new Date(v).toLocaleDateString('da-DK', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'; },
			fmtTime(d) { return d.toLocaleDateString('da-DK', { day: '2-digit', month: 'short', year: 'numeric' }); },
			typeLabel(t) { return { order: 'Ordre', quote: 'Tilbud', task: 'Opgave', email: 'E-mail', sms: 'SMS' }[t] || t; },
			parseLines(v) { if (!v) return []; if (Array.isArray(v)) return v; try { return JSON.parse(v); } catch (e) { return []; } },
			quoteTotal(q) { return this.parseLines(q.order_lines).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.price) || 0), 0); },
			filterRows(rows, search, fields) {
				const s = (search || '').trim().toLowerCase();
				if (!s) return rows;
				return rows.filter(r => fields(r).join(' ').toLowerCase().indexOf(s) !== -1);
			},
			actClear() { this.activityKinds.forEach(k => { this.actTypes[k] = false; }); },
			actOpenable(e) { return e.type === 'order' || e.type === 'quote' || e.type === 'task'; },
			actOpen(e) { if (this.actOpenable(e) && e.id) this.$emit('open', { kind: e.type, id: e.id, type: e.taskType }); }
		}
	};
</script>

<style lang="scss" scoped>
	.cw-content {
		font-family: 'Montserrat', sans-serif;
		color: var(--cw-text);
		padding: 20px;
	}
	.cw-content.cw-theme-dark {
		--cw-surface: #202127; --cw-border: #2d2f36; --cw-text: #ffffff; --cw-muted: #a0a3ac; --cw-accent: #f7223f;
	}
	.cw-content.cw-theme-light {
		--cw-surface: #ffffff; --cw-border: #ececec; --cw-text: #1c1d21; --cw-muted: #8a8d96; --cw-accent: #f7223f;
	}

	.cw-section { padding: 20px 0; border-top: 1px solid var(--cw-border); }
	.cw-section:first-child { padding-top: 0; border-top: none; }

	.cw-head { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--cw-muted); margin-bottom: 16px; }
	.cw-empty { color: var(--cw-muted); font-size: 14px; padding: 10px 0; }
	.cw-muted { color: var(--cw-muted); }

	.cw-figs { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 20px; }
	.cw-fig__l { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--cw-muted); }
	.cw-fig__v { font-size: 14px; margin-top: 4px; }

	.cw-line { display: flex; justify-content: space-between; gap: 15px; padding: 8px 0; font-size: 14px; }

	.cw-search {
		width: 100%; box-sizing: border-box; background: var(--cw-surface); border: 1px solid var(--cw-border);
		color: var(--cw-text); font-family: inherit; font-size: 14px; height: 40px; padding: 0 15px; outline: none; margin-bottom: 16px;
	}
	.cw-search::placeholder { color: var(--cw-muted); }

	.cw-row {
		display: flex; justify-content: space-between; align-items: center; gap: 15px;
		border: 1px solid var(--cw-border); padding: 12px 15px; margin-bottom: 8px; cursor: pointer; font-size: 14px;
	}
	.cw-row:hover { border-color: var(--cw-accent); }
	.cw-row.is-muted { opacity: 0.4; }
	.cw-row__main { display: flex; flex-direction: column; gap: 3px; font-weight: 600; min-width: 0; }
	.cw-row__sub { font-size: 12px; font-weight: 400; color: var(--cw-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.cw-row__val { white-space: nowrap; }

	.cw-segline { display: flex; gap: 20px; margin-bottom: 16px; }
	.cw-seg { font-size: 14px; color: var(--cw-muted); line-height: 28px; cursor: pointer; border-bottom: 2px solid transparent; }
	.cw-seg.is-on { color: var(--cw-text); border-bottom-color: var(--cw-accent); }

	.cw-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
	.cw-chip { font-size: 13px; color: var(--cw-muted); border: 1px solid var(--cw-border); padding: 6px 14px; cursor: pointer; }
	.cw-chip.is-on { background: var(--cw-accent); border-color: var(--cw-accent); color: #fff; }

	.cw-loader { display: flex; align-items: center; justify-content: center; min-height: 200px; }
	.cw-spinner { width: 28px; height: 28px; border-radius: 50%; border: 3px solid var(--cw-border); border-top-color: var(--cw-accent); animation: cw-spin 0.8s linear infinite; }
	@keyframes cw-spin { to { transform: rotate(360deg); } }
</style>
